/**
 * @yjsgames/diag-log — 도메인별 런타임 제어 DIAG 로그 레지스트리 (순수 TS, 의존 0).
 *
 * DIAG 로그를 코드에 **상시 심어두고** 레벨로 켜고 끄는 모델 — 진단 때마다 로그를 다시
 * 심는 비용을 없앤다(이전에 심은 포인트의 재탑재 = 레벨 변경 1회). 도메인 키는 boot 패러다임의
 * 설치자 key와 일치시키는 컨벤션 (예: "economy"/"dungeon"/"settings").
 *
 * - 레벨: off < diag < verbose. 기본 off (verbose-logging 규약 — 영구 보존 로그는 게이트 뒤에).
 * - 포맷: `[domain][D|V][member] ...` — 기존 `[ClassName][V][MemberName]` 규약과 호환.
 * - 런타임 제어: setLevel(domain|"*", level). 영속은 PersistPort 주입(DIP — 앱이 localStorage 어댑터).
 * - 외부 의존(콘솔/저장)은 전부 주입 가능 — vitest 순수 테스트.
 */

export type DiagLevel = "off" | "diag" | "verbose";

export const DIAG_LEVELS = ["off", "diag", "verbose"] as const;

const LEVEL_RANK: Record<DiagLevel, number> = { off: 0, diag: 1, verbose: 2 };

export function isDiagLevel(value: string): value is DiagLevel {
  return (DIAG_LEVELS as readonly string[]).includes(value);
}

/** 레벨 맵 영속 Port — 앱이 localStorage 등으로 구현 주입 (새로고침 후 레벨 유지). */
export interface DiagPersistPort {
  load(): Record<string, DiagLevel> | null;
  save(levels: Record<string, DiagLevel>): void;
}

export interface DiagRegistryOptions {
  /** 미지정 도메인의 기본 레벨 — 기본 "off". */
  defaultLevel?: DiagLevel;
  /** 출력 sink — 기본 console.log. 테스트/캡처 주입용. */
  sink?: (line: string) => void;
  /** 레벨 맵 영속 — 미주입 시 휘발(세션 한정). */
  persist?: DiagPersistPort;
}

export interface DiagLogger {
  readonly domain: string;
  /** 진단 포인트 — 레벨 ≥ diag일 때 `[domain][D][member] ...` 출력. */
  diag(member: string, ...args: unknown[]): void;
  /** 고빈도 상태 덤프 — 레벨 ≥ verbose일 때 `[domain][V][member] ...` 출력. */
  verbose(member: string, ...args: unknown[]): void;
  /** 비용 큰 인자 조립 가드 — `if (log.isOn("diag")) log.diag(...)` 패턴. */
  isOn(level: Exclude<DiagLevel, "off">): boolean;
}

export class DiagLogRegistry {
  private readonly overrides = new Map<string, DiagLevel>();
  private readonly loggers = new Map<string, DiagLogger>();
  private defaultLevel: DiagLevel;
  private readonly sink: (line: string) => void;
  private readonly persist: DiagPersistPort | undefined;

  constructor(options: DiagRegistryOptions = {}) {
    this.defaultLevel = options.defaultLevel ?? "off";
    // eslint 무관 — console은 sink 기본값일 뿐, 주입으로 대체 가능.
    this.sink = options.sink ?? ((line) => console.log(line));
    this.persist = options.persist;
    const saved = this.persist?.load();
    if (saved) {
      for (const [domain, level] of Object.entries(saved)) {
        if (isDiagLevel(level)) this.overrides.set(domain, level);
      }
    }
  }

  /** 도메인 로거 생성/캐시 — boot key = domain 컨벤션. 호출만으로 domains()에 노출(발견성). */
  logger(domain: string): DiagLogger {
    const cached = this.loggers.get(domain);
    if (cached) return cached;
    const levelOf = () => this.levelOf(domain);
    const sink = this.sink;
    const log = (tag: "D" | "V", member: string, args: unknown[]): void => {
      sink(`[${domain}][${tag}][${member}]${args.length ? " " + args.map(formatArg).join(" ") : ""}`);
    };
    const created: DiagLogger = {
      domain,
      diag(member, ...args) {
        if (LEVEL_RANK[levelOf()] >= LEVEL_RANK.diag) log("D", member, args);
      },
      verbose(member, ...args) {
        if (LEVEL_RANK[levelOf()] >= LEVEL_RANK.verbose) log("V", member, args);
      },
      isOn(level) {
        return LEVEL_RANK[levelOf()] >= LEVEL_RANK[level];
      },
    };
    this.loggers.set(domain, created);
    return created;
  }

  /** 레벨 변경 — domain="*"는 기본 레벨 교체 + 도메인별 override 전부 제거(전체 켜기/끄기 멘탈 모델). */
  setLevel(domain: string, level: DiagLevel): void {
    if (domain === "*") {
      this.defaultLevel = level;
      this.overrides.clear();
    } else {
      this.overrides.set(domain, level);
    }
    this.persist?.save(this.snapshot());
  }

  levelOf(domain: string): DiagLevel {
    return this.overrides.get(domain) ?? this.defaultLevel;
  }

  /** logger()가 생성된 도메인 목록 — "어떤 도메인을 켤 수 있나" 발견용. */
  domains(): string[] {
    return [...this.loggers.keys()];
  }

  /** 도메인별 override 레벨 맵 (영속/덤프용 — 기본 레벨 도메인은 미포함). */
  snapshot(): Record<string, DiagLevel> {
    return Object.fromEntries(this.overrides);
  }
}

/** 인자 직렬화 — 객체는 JSON(실패 시 String), 원시값은 그대로. 로그 1줄 유지를 위해 압축 직렬화. */
function formatArg(arg: unknown): string {
  if (typeof arg === "string") return arg;
  if (typeof arg === "object" && arg !== null) {
    try {
      return JSON.stringify(arg);
    } catch {
      return String(arg);
    }
  }
  return String(arg);
}
